import React, { useCallback, useMemo } from 'react';
import { useMakeSelector } from 'hooks/use-make-selector';
import {
  makeGetCategoryParentInfoByProductId,
  makeGetProductById,
} from 'redux/modules/entities/selectors/products';
import { getLocation, isSuppressAlternatives } from 'redux/modules/routing/selectors';
import { getIsFavourite } from 'redux/modules/favourites-products/selectors';
import { isSponsoredProduct } from 'redux/modules/search-and-browse/selectors';
import { viewAlternativesModal } from 'constants/modals';
import { PICKING_REF_VIEW_ALTERNATIVES } from 'constants/picking-ref';

import Button from '@johnlewispartnership/wtr-ingredients/ingredients/Button';

import { NotifyModalArgs, notifyModalOpen } from 'redux/modules/common-modal/actions';
import { toggleProductModal } from 'redux/modules/trolley/actions/toggle-product-modal';
import { getSubstitutionProducts } from 'redux/modules/trolley/actions/get-substitution-products';

import { useSelector, useDispatch } from 'react-redux';
import { handleProductListClick } from 'utils/gtm';
import { ListAnalytics } from 'analytics/types';
import { useWtrSelector } from 'redux/hooks';
import { getPromotionsByIds } from 'redux/modules/entities/selectors/promotions';
import isEqual from 'lodash/isEqual';
import { POD_TYPE_REGULAR, POD_TYPE_SPONSORED } from 'components/ProductPod2/utils';
import { getProductPromotions } from 'redux/modules/product-details/selectors/product-pricing';
import amendingOrder from 'redux/modules/trolley/selectors/amending-order';

export interface ViewAlternativesProps {
  productId: string;
  conflictMessage: string;
  position: number;
  listDefinitions: ListAnalytics;
}

export const trackViewAlternativesClick =
  (
    productData: unknown,
    analyticsData: {
      isFavourite: boolean;
      position: number;
      sponsored: boolean;
      sponsorshipId: string;
      conflictMessage: string;
      podType: string;
      promotions: unknown[];
      purchaseOrAmend?: string;
      listDefinitions: ListAnalytics;
    },
  ) =>
  () => {
    handleProductListClick({
      conflictMessage: analyticsData?.conflictMessage?.toLowerCase(),
      isFavourite: analyticsData?.isFavourite,
      productData,
      position: analyticsData?.position,
      boosted: false,
      sponsored: analyticsData?.sponsored,
      // @ts-expect-error handleProductListClick() has not been typed
      sponsorshipId: analyticsData?.sponsorshipId,
      listDefinitions: analyticsData?.listDefinitions,
      podType: analyticsData?.podType,
      promotions: analyticsData?.promotions,
      purchaseOrAmend: analyticsData?.purchaseOrAmend,
    });
  };

const ViewAlternatives = ({
  productId,
  conflictMessage,
  position,
  listDefinitions,
}: ViewAlternativesProps) => {
  const dispatch: WtrDispatch = useDispatch();
  const { id, url } = useMakeSelector(makeGetCategoryParentInfoByProductId, productId);
  const { pathname } = useSelector(getLocation);
  const suppressDisplayLink = useSelector(isSuppressAlternatives);
  const productData = useMakeSelector(makeGetProductById, productId);
  const { lineNumber, name, sponsorshipId } = productData;
  // eslint-disable-next-line
  // @ts-ignore: createSelector known issue, where the return function is typed as receiving a single arg
  const isFavourite = useSelector(state => getIsFavourite(state, lineNumber));
  const sponsored = useSelector(state => isSponsoredProduct(state, lineNumber));

  const isAmending = useWtrSelector(amendingOrder);
  // @ts-expect-error Expected 1 arguments, but got 2
  const promotionIds = useWtrSelector(state => getProductPromotions(state, productId), isEqual);
  const promotions = useWtrSelector(
    // @ts-expect-error Expected 1 arguments, but got 2
    state => getPromotionsByIds(state, promotionIds || []),
    isEqual,
  );
  const podType = sponsored ? POD_TYPE_SPONSORED : POD_TYPE_REGULAR;

  const analyticsData = useMemo(
    () => ({
      isFavourite,
      position,
      sponsored,
      sponsorshipId,
      conflictMessage,
      podType,
      promotions,
      purchaseOrAmend: isAmending ? 'amend' : 'purchase',
      listDefinitions,
    }),
    [
      conflictMessage,
      isFavourite,
      position,
      sponsored,
      sponsorshipId,
      promotions,
      podType,
      isAmending,
      listDefinitions,
    ],
  );

  const display = id && pathname !== url && !suppressDisplayLink;

  const getAlternativeProductsCall = useCallback(
    (productLineNumber: number, origin: string) =>
      dispatch(getSubstitutionProducts(productLineNumber, origin, PICKING_REF_VIEW_ALTERNATIVES)),
    [dispatch],
  );
  const toggleModal = useCallback(
    (productLineNumber: number, isOpen: boolean) =>
      dispatch(toggleProductModal(productLineNumber, null, isOpen, viewAlternativesModal.id)),
    [dispatch],
  );
  const notifyModalCall = useCallback(
    (props: NotifyModalArgs | undefined) => dispatch(notifyModalOpen(props)),
    [dispatch],
  );

  const handleButtonClick = useCallback(() => {
    toggleModal(lineNumber, true);
    getAlternativeProductsCall(lineNumber, 'view-alternatives');
    notifyModalCall({
      ...viewAlternativesModal,
      title: viewAlternativesModal.titleText,
    });
    dispatch(trackViewAlternativesClick(productData, analyticsData));
  }, [
    toggleModal,
    getAlternativeProductsCall,
    notifyModalCall,
    dispatch,
    lineNumber,
    productData,
    analyticsData,
  ]);

  if (!display) {
    return null;
  }

  return (
    <Button
      data-testid="view-alternatives-toggle"
      onClick={handleButtonClick}
      type="button"
      theme="secondary"
      width="full"
    >
      View alternatives<span className="sr-only">{` for ${name}`}</span>
    </Button>
  );
};

export default ViewAlternatives;
